<template>
  <Head title="Aanmelden als VIP - Word creator op ShoutOut.vip">
    <meta
      name="description"
      content="Verdien aan je bereik door shoutouts van jezelf te verkopen aan merken en fans. Meld je aan als VIP op ShoutOut.vip."
    >
    <link
      rel="canonical"
      :href="localeRoute('frontend.vip-signup')"
    >
    <meta
      property="og:image"
      content="/frontend/assets/favicon/og-image.jpg"
    >
  </Head>

  <section class="vip-signup-hero py-20 relative overflow-hidden">
    <img
      src="/frontend/assets/mobile-hero-swirl.svg"
      alt="decoratie"
      class="absolute top-0 left-0 lg:hidden"
    >
    <img
      src="/frontend/assets/desktop-hero-swirl.svg"
      alt="decoratie"
      class="absolute top-0 left-0 hidden lg:block"
    >

    <div class="container">
      <div class="text-center px-8">
        <h1 class="text-5xl font-bold lg:text-7xl">
          Word creator op Shoutout.vip
        </h1>

        <p class="text-gray-400 mt-2 lg:text-xl mb-0 lg:w-3/5 lg:mx-auto">
          Verdien aan je bereik door shoutouts van jezelf te verkopen aan merken en fans. Op Shoutout.vip zet je jouw aanbod eenvoudig online, bepaal je je prijs en kies je zelf welke aanvragen je accepteert.
        </p>
      </div>
    </div>
  </section>

  <section class="vip-signup-benefits py-20 md:py20 bg-white overflow-hidden relative">
    <div class="container">
      <h2 class="text-4xl font-bold text-center mb-8 lg:text-5xl">
        Wat je krijgt
      </h2>
      <ul class="max-w-3xl mx-auto space-y-4 text-lg">
        <li class="flex items-center justify-center">
          <span class="text-red text-2xl mr-3">✓</span>
          <span>Inkomsten met shoutouts die bij jou passen</span>
        </li>
        <li class="flex items-center justify-center">
          <span class="text-red text-2xl mr-3">✓</span>
          <span>Jij bepaalt je prijs en beschikbaarheid</span>
        </li>
        <li class="flex items-center justify-center">
          <span class="text-red text-2xl mr-3">✓</span>
          <span>Meer aanvragen via een professioneel profiel</span>
        </li>
        <li class="flex items-center justify-center">
          <span class="text-red text-2xl mr-3">✓</span>
          <span>Snel live: aanmelden → profiel check → verkoop</span>
        </li>
      </ul>
    </div>
  </section>

  <section
    v-if="!form.wasSuccessful"
    class="vip-signup-form pb-10 md:pb-20 bg-white overflow-hidden relative"
  >
    <img
      src="/frontend/assets/scribble-round-desktop.svg"
      alt="decoratie"
      class="hidden lg:block absolute left-0 top-[500px]"
    >

    <div class="container">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-4xl font-bold text-center mb-8 lg:text-5xl">
          Gegevens voor je profiel
        </h2>

        <form @submit.prevent="submit">
          <div class="flex flex-wrap relative">
            <div class="w-full mb-4 md:w-1/2 md:pr-2">
              <InputField
                v-model="form.name"
                :form="form"
                name="name"
                type="text"
                label="Naam / artiestennaam"
                placeholder="Jan Bakker"
              />
            </div>

            <div class="w-full mb-4 md:w-1/2 md:pl-2">
              <InputField
                v-model="form.email"
                :form="form"
                name="email"
                type="email"
                label="E-mailadres"
                placeholder="janbakker@gmail.com"
              />
            </div>

            <div class="w-full mb-4">
              <InputField
                v-model="form.phone"
                :form="form"
                name="phone"
                type="tel"
                label="Telefoonnummer"
                placeholder="06 12 34 56 78"
              />
            </div>

            <div class="w-full mb-4">
              <TextareaField
                v-model="form.bio"
                :form="form"
                name="bio"
                label="Bio (korte omschrijving over jou)"
                placeholder="Vertel kort iets over jezelf..."
                rows="5"
              />
            </div>

            <div class="w-full mb-6">
              <label class="block text-gray-700 text-sm font-bold mb-2">
                Socials (minimaal 1 link delen)
              </label>

              <div class="space-y-3">
                <InputField
                  v-model="form.instagram"
                  :form="form"
                  name="instagram"
                  type="url"
                  label="Instagram"
                  placeholder="https://instagram.com/jouwaccount"
                  :is-required="false"
                />

                <InputField
                  v-model="form.tiktok"
                  :form="form"
                  name="tiktok"
                  type="url"
                  label="TikTok"
                  placeholder="https://tiktok.com/@jouwaccount"
                  :is-required="false"
                />

                <InputField
                  v-model="form.youtube"
                  :form="form"
                  name="youtube"
                  type="url"
                  label="YouTube"
                  placeholder="https://youtube.com/@jouwkanaal"
                  :is-required="false"
                />

                <InputField
                  v-model="form.linkedin"
                  :form="form"
                  name="linkedin"
                  type="url"
                  label="LinkedIn"
                  placeholder="https://linkedin.com/in/jouwprofiel"
                  :is-required="false"
                />
              </div>

              <p
                v-if="form.errors.socials"
                class="text-red text-xs italic mt-2"
              >
                {{ form.errors.socials }}
              </p>
            </div>

            <div class="w-full mb-4 md:w-1/2 md:pr-2">
              <InputField
                v-model="form.price"
                :form="form"
                name="price"
                type="number"
                label="Prijs per ShoutOut (in €)"
                placeholder="50"
              />
            </div>

            <div class="w-full mb-4">
              <label class="flex items-start">
                <input
                  v-model="form.terms_accepted"
                  type="checkbox"
                  class="mt-1 mr-2"
                  required
                >
                <span class="text-sm">
                  Ik ga akkoord met het verwerken van mijn gegevens voor het aanmaken van mijn ShoutOut.vip-profiel.
                </span>
              </label>
              <p
                v-if="form.errors.terms_accepted"
                class="text-red text-xs italic mt-1"
              >
                {{ form.errors.terms_accepted }}
              </p>
            </div>

            <div class="mb-4">
              <VueRecaptcha
                ref="recaptcha"
                :sitekey="recaptchaSiteKey"
                @verify="(response) => form.recaptcha = response"
                @expired="() => form.recaptcha = ''"
              />

              <p
                v-if="form.errors.recaptcha"
                class="text-red text-xs italic"
              >
                {{ form.errors.recaptcha }}
              </p>
            </div>

            <div class="w-full text-center">
              <button
                type="submit"
                class="button-black shadow-button-black !text-base w-auto inline-block !px-16 mx-auto"
                :disabled="form.processing"
              >
                <span v-if="form.processing">
                  <img
                    src="/frontend/assets/spinner.svg"
                    alt="laden"
                    class="h-6 w-6 animate-spin"
                  >
                </span>
                <span v-else>
                  Versturen
                </span>
              </button>
            </div>

            <img
              src="/frontend/assets/scribble-round-desktop.svg"
              alt="decoratie"
              class="absolute -right-[45px] -bottom-[60px] md:hidden"
            >
          </div>
        </form>
      </div>
    </div>
  </section>

  <section
    v-else
    class="vip-signup-success py-10 md:py-20 bg-gray-50 overflow-hidden relative"
  >
    <div class="container">
      <div class="max-w-4xl mx-auto">
        <div class="text-center mb-10">
          <div class="text-6xl mb-4">
            🎉
          </div>
          <h2 class="text-4xl font-bold mb-4 lg:text-5xl text-red">
            Bedankt voor je aanmelding!
          </h2>
        </div>

        <div class="bg-white p-8 rounded-lg shadow-lg">
          <h3 class="text-2xl font-bold mb-6">
            Wat gebeurt er na je aanmelding?
          </h3>

          <ol class="space-y-4">
            <li class="flex items-start">
              <span class="flex-shrink-0 w-8 h-8 rounded-full bg-red text-white flex items-center justify-center font-bold mr-4">
                1
              </span>
              <p class="text-gray-700 pt-1">
                Zodra we alles binnen hebben, checken we je gegevens en maken we jouw profiel aan.
              </p>
            </li>
            <li class="flex items-start">
              <span class="flex-shrink-0 w-8 h-8 rounded-full bg-red text-white flex items-center justify-center font-bold mr-4">
                2
              </span>
              <p class="text-gray-700 pt-1">
                Je ontvangt een persoonlijke link. Die promoot je eenmalig om je profiel actief te maken.
              </p>
            </li>
            <li class="flex items-start">
              <span class="flex-shrink-0 w-8 h-8 rounded-full bg-red text-white flex items-center justify-center font-bold mr-4">
                3
              </span>
              <p class="text-gray-700 pt-1">
                Je krijgt toegang tot een app waarmee je ShoutOuts kunt opnemen (met of zonder autocue).
              </p>
            </li>
            <li class="flex items-start">
              <span class="flex-shrink-0 w-8 h-8 rounded-full bg-red text-white flex items-center justify-center font-bold mr-4">
                4
              </span>
              <p class="text-gray-700 pt-1">
                Komt er een aanvraag binnen? Dan heb je maximaal 7 dagen om te leveren. We streven naar binnen 48 uur, liefst zo snel mogelijk.
              </p>
            </li>
            <li class="flex items-start">
              <span class="flex-shrink-0 w-8 h-8 rounded-full bg-red text-white flex items-center justify-center font-bold mr-4">
                5
              </span>
              <p class="text-gray-700 pt-1">
                Zit er een aanvraag bij die je niet wilt doen (om welke reden dan ook)? Dan kun je die weigeren via de app en krijgt de klant automatisch het geld terug.
              </p>
            </li>
          </ol>

          <p class="mt-8 text-xl font-bold text-center text-red">
            Alvast welkom aan boord.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {useForm} from '@inertiajs/vue3';
import { VueRecaptcha } from 'vue-recaptcha';
import InputField from '../../Components/Form/InputField.vue';
import TextareaField from '../../Components/Form/TextareaField.vue';

export default {
  components: {
    VueRecaptcha,
    InputField,
    TextareaField,
  },

  props: {
    recaptchaSiteKey: {
      type: String,
      required: true,
    },
  },

  setup() {
    const form = useForm({
      name: '',
      email: '',
      phone: '',
      bio: '',
      instagram: '',
      tiktok: '',
      youtube: '',
      linkedin: '',
      price: '',
      terms_accepted: false,
      recaptcha: '',
    });

    return {form};
  },

  methods: {
    submit() {
      if (this.form.processing) {
        return;
      }

      this.form.post(this.localeRoute('frontend.vip-signup.store'), {
        onSuccess: () => {
          // Keep the form data to show success state
          // Don't reset so we can show the success message
        },
        onError: () => {
          // Reset reCAPTCHA on validation error
          this.form.recaptcha = '';
          if (this.$refs.recaptcha) {
            this.$refs.recaptcha.reset();
          }
        },
      });
    },
  },
};
</script>

